import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebase';
import { usePostHog } from 'posthog-js/react';
import { validateEmailSyntax, suggestEmailCorrection } from '../utils/emailValidation';
import './EmailRegistrationCard.css';

const EmailRegistrationCard = ({ 
    user, 
    onRewardClaimed = null,
    alwaysVisible = false
}) => {
    const { t } = useTranslation();
    const posthog = usePostHog();
    const [emailRewardStatus, setEmailRewardStatus] = useState({ claimed: false, email: null });
    const [isLoadingEmailStatus, setIsLoadingEmailStatus] = useState(true);
    const [emailForCredits, setEmailForCredits] = useState('');
    const [isSubmittingEmailForCredits, setIsSubmittingEmailForCredits] = useState(false);
    const [emailCreditsError, setEmailCreditsError] = useState('');
    const [emailSuggestion, setEmailSuggestion] = useState(null);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);
    const [emailRegistrationCredits] = useState(5);
    const [claimedInCurrentSession, setClaimedInCurrentSession] = useState(false);

    // Load email reward status
    const loadEmailRewardStatus = async () => {
        if (!user) {
            setIsLoadingEmailStatus(false);
            return;
        }

        setIsLoadingEmailStatus(true);
        try {
            const getCreditRewardStatusFunc = httpsCallable(functions, 'getCreditRewardStatus');
            const result = await getCreditRewardStatusFunc();
            if (result.data.email.claimed) {
                setEmailRewardStatus({
                    claimed: true,
                    email: result.data.email.email,
                    claimedAt: result.data.email.claimedAt
                });
            }
        } catch (error) {
            console.error("Error loading email reward status:", error);
        } finally {
            setIsLoadingEmailStatus(false);
        }
    };

    // Typo hint, computed on blur so it doesn't flicker mid-typing.
    // Purely advisory: it never blocks submission. See suggestEmailCorrection.
    const handleEmailBlur = () => {
        const suggestion = suggestEmailCorrection(emailForCredits);
        setEmailSuggestion(suggestion);
        if (suggestion) {
            posthog?.capture('email_suggestion_shown', {
                suggested_domain: suggestion.suggestedDomain,
                component: 'EmailRegistrationCard'
            });
        }
    };

    const handleEmailChange = (e) => {
        setEmailForCredits(e.target.value);
        setEmailSuggestion(null);
        setPendingConfirmation(null);
    };

    const applySuggestion = () => {
        if (!emailSuggestion) return;
        setEmailForCredits(emailSuggestion.suggestion);
        setEmailSuggestion(null);
        setEmailCreditsError('');
        posthog?.capture('email_suggestion_accepted', {
            suggested_domain: emailSuggestion.suggestedDomain,
            component: 'EmailRegistrationCard'
        });
    };

    // Claim with a concrete address. Separate from the submit handler so the
    // typo-confirm buttons can claim a value that is not yet in state.
    const performClaim = async (address) => {
        setIsSubmittingEmailForCredits(true);
        
        try {
            const claimEmailCreditsFunc = httpsCallable(functions, 'claimEmailRegistrationCredits');
            const result = await claimEmailCreditsFunc({ email: address });

            if (result.data.success) {
                setEmailRewardStatus({
                    claimed: true,
                    email: address,
                    claimedAt: new Date()
                });
                setClaimedInCurrentSession(true);

                // Track successful email credit claim
                posthog?.capture('email_credits_claimed', {
                    email: address,
                    credits_earned: result.data.creditsAwarded || emailRegistrationCredits,
                    page: 'home'
                });

                if (onRewardClaimed) {
                    onRewardClaimed();
                }
            } else {
                setEmailCreditsError(result.data.error || 'Failed to claim diamonds');
                // Track error
                posthog?.capture('email_credits_error', {
                    error_message: result.data.error || 'Failed to claim diamonds',
                    component: 'EmailRegistrationCard',
                    page: 'home'
                });
            }
        } catch (error) {
            console.error('Error claiming email credits:', error);
            // functions/invalid-argument is the server rejecting the address
            // itself (e.g. unknown TLD), not a transient failure.
            if (error.code === 'functions/invalid-argument') {
                setEmailCreditsError(t('emailRegistration.errorInvalidEmail', 'Please enter a valid email address'));
            } else {
                setEmailCreditsError('Failed to claim diamonds. Please try again.');
            }
            // Track error
            posthog?.capture('email_credits_error', {
                error_message: error.message,
                component: 'EmailRegistrationCard',
                page: 'home'
            });
        } finally {
            setIsSubmittingEmailForCredits(false);
        }
    };

    // Handle email credits submission
    const handleEmailCreditsSubmit = async (e) => {
        e.preventDefault();
        setEmailCreditsError('');

        const { valid, normalized } = validateEmailSyntax(emailForCredits);
        if (!valid) {
            setEmailCreditsError(t('emailRegistration.errorInvalidEmail', 'Please enter a valid email address'));
            return;
        }

        // Recomputed at submit time: the blur hint can be skipped entirely by
        // an Enter-key submit. A suspected typo pauses the claim until the user
        // explicitly picks an address; it still never hard-blocks.
        const suggestion = suggestEmailCorrection(normalized);
        if (suggestion) {
            setEmailSuggestion(null);
            setPendingConfirmation({
                typed: normalized,
                suggestion: suggestion.suggestion,
                suggestedDomain: suggestion.suggestedDomain
            });
            posthog?.capture('email_typo_confirm_shown', {
                suggested_domain: suggestion.suggestedDomain,
                component: 'EmailRegistrationCard'
            });
            return;
        }

        await performClaim(normalized);
    };

    const acceptSuggestedEmail = async () => {
        if (!pendingConfirmation) return;
        const address = pendingConfirmation.suggestion;
        setEmailForCredits(address);
        setPendingConfirmation(null);
        setEmailSuggestion(null);
        posthog?.capture('email_typo_confirm_accepted', {
            suggested_domain: pendingConfirmation.suggestedDomain,
            component: 'EmailRegistrationCard'
        });
        await performClaim(address);
    };

    const keepTypedEmail = async () => {
        if (!pendingConfirmation) return;
        const address = pendingConfirmation.typed;
        setPendingConfirmation(null);
        setEmailSuggestion(null);
        posthog?.capture('email_typo_confirm_overridden', {
            suggested_domain: pendingConfirmation.suggestedDomain,
            component: 'EmailRegistrationCard'
        });
        await performClaim(address);
    };

    useEffect(() => {
        loadEmailRewardStatus();
    }, [user]);

    // Don't render if loading
    if (isLoadingEmailStatus) {
        return null;
    }

    // Hide the card if already claimed and not claimed in current session
    if (!alwaysVisible && emailRewardStatus.claimed && !claimedInCurrentSession) {
        return null;
    }

    return (
        <div className={`reward-card email-reward-card ${emailRewardStatus.claimed ? 'claimed' : ''}`}>
            <div className="reward-content">
                <div className="reward-header">
                    <h4>📧 {emailRewardStatus.claimed ? t('emailRegistration.emailVerified', 'Email Verified') : t('emailRegistration.verifyYourEmail', 'Verify your email')}</h4>
                    <span className="reward-value">+{emailRegistrationCredits} 💎</span>
                </div>
                <p>{emailRewardStatus.claimed ? t('emailRegistration.claimedDiamonds', "You've claimed your diamonds!") : t('emailRegistration.verifyEmailDesc', 'Verify your email address to receive 5 bonus diamonds')}</p>
                
                <form onSubmit={emailRewardStatus.claimed ? undefined : handleEmailCreditsSubmit} className="reward-form">
                    <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        autoCapitalize="none"
                        spellCheck={false}
                        maxLength={254}
                        placeholder={emailRewardStatus.claimed ? emailRewardStatus.email : t('emailRegistration.enterYourEmail', 'Enter your email')}
                        value={emailRewardStatus.claimed ? emailRewardStatus.email : emailForCredits}
                        onChange={emailRewardStatus.claimed ? undefined : handleEmailChange}
                        onBlur={emailRewardStatus.claimed ? undefined : handleEmailBlur}
                        disabled={emailRewardStatus.claimed || isSubmittingEmailForCredits}
                        required
                        readOnly={emailRewardStatus.claimed}
                        className="reward-input"
                    />
                    <button
                        type="submit"
                        disabled={emailRewardStatus.claimed || isSubmittingEmailForCredits || !emailForCredits.trim()}
                        className="reward-button"
                    >
                        {emailRewardStatus.claimed ? 
                            t('emailRegistration.claimed', 'Claimed') :
                            (isSubmittingEmailForCredits ? t('emailRegistration.claiming', 'Claiming...') : t('emailRegistration.claimDiamonds', `Claim ${emailRegistrationCredits} 💎`, { count: emailRegistrationCredits }))
                        }
                    </button>
                </form>
                {emailCreditsError && !emailRewardStatus.claimed && (
                    <div className="reward-error">
                        {emailCreditsError}
                    </div>
                )}
                {emailSuggestion && !pendingConfirmation && !emailRewardStatus.claimed && (
                    <div className="reward-suggestion">
                        <span>{t('emailRegistration.didYouMean', 'Did you mean')}</span>
                        <button
                            type="button"
                            className="reward-suggestion-button"
                            onClick={applySuggestion}
                        >
                            {emailSuggestion.suggestion}
                        </button>
                        <span>?</span>
                    </div>
                )}
                {pendingConfirmation && !emailRewardStatus.claimed && (
                    <div className="reward-confirm">
                        <span>{t('emailRegistration.confirmTypoTitle', 'That address looks like it might have a typo.')}</span>
                        <div className="reward-confirm-actions">
                            <button
                                type="button"
                                className="reward-confirm-button"
                                onClick={acceptSuggestedEmail}
                                disabled={isSubmittingEmailForCredits}
                            >
                                {t('emailRegistration.useSuggested', `Use ${pendingConfirmation.suggestion}`, { email: pendingConfirmation.suggestion })}
                            </button>
                            <button
                                type="button"
                                className="reward-confirm-button secondary"
                                onClick={keepTypedEmail}
                                disabled={isSubmittingEmailForCredits}
                            >
                                {t('emailRegistration.keepTyped', `Claim with ${pendingConfirmation.typed}`, { email: pendingConfirmation.typed })}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailRegistrationCard;

